/**
 * LRU tracker for recently visited channels, persisted per user in localStorage.
 *
 * Drives the preload ordering in Sidebar: the 15 channels the user has most
 * recently clicked into are the ones preloaded — a much tighter signal of
 * "where I'll go next" than workspace-wide activity ordering.
 *
 * Storage key: `ano-recent-channels:${userId}`
 * Shape: `Record<string /* channelId *\/, number /* lastVisitedMs *\/>`
 * Bounded: newest `MAX_ENTRIES` retained on every write — keeps localStorage
 * tidy even for heavy channel hoppers.
 */

const STORAGE_PREFIX = "ano-recent-channels:";
const MAX_ENTRIES = 50;

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function readMap(userId: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, number>;
    }
  } catch {
    // Malformed JSON or localStorage unavailable — fall through.
  }
  return {};
}

function writeMap(userId: string, map: Record<string, number>): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(map));
  } catch {
    // localStorage full / unavailable — best-effort, skip.
  }
}

/**
 * Mark `channelId` as just-visited for `userId`. Prunes to the newest
 * `MAX_ENTRIES` on write to cap storage growth.
 */
export function recordChannelVisit(
  userId: string,
  channelId: string,
  now: number = Date.now(),
): void {
  if (!userId || !channelId) return;
  const map = readMap(userId);
  map[channelId] = now;

  // Prune to newest MAX_ENTRIES
  const entries = Object.entries(map);
  if (entries.length > MAX_ENTRIES) {
    entries.sort((a, b) => b[1] - a[1]);
    const pruned: Record<string, number> = {};
    for (let i = 0; i < MAX_ENTRIES; i++) {
      const [id, ts] = entries[i];
      pruned[id] = ts;
    }
    writeMap(userId, pruned);
    return;
  }

  writeMap(userId, map);
}

/**
 * Return up to `limit` channel IDs for `userId`, most-recently-visited first.
 * Empty array if no history (e.g. new user / first session in this browser).
 */
export function getRecentChannelIds(userId: string, limit: number): string[] {
  if (!userId || limit <= 0) return [];
  const map = readMap(userId);
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([id]) => id);
}
